/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable react/button-has-type */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Alert, Box, Snackbar, Typography } from '@mui/material';

import {
  useListProductsQuery,
  useValidateUpgradeSerialMutation,
  useValidateCrossgradeSerialMutation,
} from 'renderer/features/products-page/services/products.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import {
  useGetCartQuery,
  useUpdateCartItemMutation,
  useRemoveCartItemMutation,
} from '../services/cart.endpoints';

import { CartLineItem } from '../cart/CartLineItem';
import { cartItemKey } from '../cart/cartItemKey';
import { CartOrderSummary } from '../cart/CartOrderSummary';
import { cartPageStyles as styles } from '../cart/cartPageStyles';
import {
  computeItemsCount,
  hasInvalidUpgradeSerials,
  hasMissingRequiredHostApp,
  isCartEmptyOrZero,
} from '../cart/cartCheckout';
import { isUpgradeItem } from '../cart/cartLineKinds';
import { computeCartTotals, getLineDisplayTotal } from '../cart/cartTotals';
import { useUpgradeSerialRows } from '../cart/useUpgradeSerialRows';
import {
  areCartControlsDisabled,
  getCartCompletedMessage,
  notifyCartMutationFeedback,
} from '../cart/cartCheckoutLock';

const baseUrl = `${process.env.SERVER_URL ?? 'http://localhost:3000'}/api`;

type PageNotice = {
  message: string;
  severity: 'warning' | 'info';
};

const CartPage = () => {
  const navigate = useNavigate();

  const {
    data: cart,
    isLoading,
    refetch,
  } = useGetCartQuery(null, {
    // Another tab may have checked out or replaced this cart; never act on a
    // cached copy from before this page mounted.
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [updateCartItemMutation] = useUpdateCartItemMutation();
  const [removeCartItemMutation] = useRemoveCartItemMutation();
  const [validateUpgradeSerial] = useValidateUpgradeSerialMutation();
  const [validateCrossgradeSerial] = useValidateCrossgradeSerialMutation();
  const [pageNotice, setPageNotice] = useState<PageNotice | null>(null);

  useEffect(() => {
    const refreshCart = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', refreshCart);
    window.addEventListener('focus', refreshCart);

    return () => {
      document.removeEventListener('visibilitychange', refreshCart);
      window.removeEventListener('focus', refreshCart);
    };
  }, [refetch]);

  const showPageNotice = useCallback(
    (message: string, severity: 'warning' | 'info') => {
      setPageNotice({ message, severity });
    },
    [],
  );

  const handleCartMutationResult = useCallback(
    async (result: unknown) => {
      const feedback = notifyCartMutationFeedback(result, showPageNotice);
      if (feedback === 'completed') {
        await refetch();
      }
      return result;
    },
    [refetch, showPageNotice],
  );

  const handleCartMutationError = useCallback(
    async (error: unknown) => {
      showPageNotice(
        formatQueryError(error) || 'Unable to update your cart right now.',
        'warning',
      );
      await refetch();
    },
    [refetch, showPageNotice],
  );

  const updateCartItem = useCallback(
    async (arg: Parameters<typeof updateCartItemMutation>[0]) => {
      try {
        const result = await updateCartItemMutation(arg).unwrap();
        return handleCartMutationResult(result);
      } catch (error) {
        await handleCartMutationError(error);
        return null;
      }
    },
    [handleCartMutationError, handleCartMutationResult, updateCartItemMutation],
  );

  const removeCartItem = useCallback(
    async (arg: Parameters<typeof removeCartItemMutation>[0]) => {
      try {
        const result = await removeCartItemMutation(arg).unwrap();
        return handleCartMutationResult(result);
      } catch (error) {
        await handleCartMutationError(error);
        return null;
      }
    },
    [handleCartMutationError, handleCartMutationResult, removeCartItemMutation],
  );

  const { data: products } = useListProductsQuery({
    includeHidden: false,
  });

  const productsById = useMemo(() => {
    const map = new Map<string, any>();
    (products ?? []).forEach((p: any) => {
      map.set(String(p._id), p);
    });
    return map;
  }, [products]);

  const getSupportedPlatformsForItem = (item: any): string[] => {
    const product =
      productsById.get(String(item.productId?._id ?? item.productId)) ??
      item.productId;

    const platforms = product?.supportedPlatforms;

    if (!Array.isArray(platforms) || platforms.length === 0) {
      return ['Windows', 'Macintosh', 'All Platforms'];
    }

    return platforms;
  };

  const {
    serialRowsByItem,
    setSerialRowsByItem,
    validateSerialForItem,
    removeSerialRow,
    commitUpgradeSerials,
    handleUpgradeQuantityChange,
  } = useUpgradeSerialRows({
    cart,
    updateCartItem,
    validateUpgradeSerial,
    validateCrossgradeSerial,
  });

  const items = useMemo(() => cart?.items ?? [], [cart]);
  const cartCompletedMessage = getCartCompletedMessage(cart);
  const cartControlsDisabled = areCartControlsDisabled(cart);

  const handleQuantityChange = async (item: any, quantity: number) => {
    if (cartControlsDisabled) {
      notifyCartMutationFeedback(cart, showPageNotice);
      return;
    }

    if (isUpgradeItem(item)) {
      await handleUpgradeQuantityChange(item, quantity);
      return;
    }

    const safeQuantity = Math.max(1, quantity);
    await updateCartItem({
      cartItemId: cartItemKey(item),
      quantity: safeQuantity,
      platform: item.platform,
      hostApplication: item.hostApplication,
    });
  };

  const handleRemove = async (cartItemId: string) => {
    if (cartControlsDisabled) {
      notifyCartMutationFeedback(cart, showPageNotice);
      return;
    }

    await removeCartItem({ cartItemId });
  };

  const handleCheckout = () => {
    navigate('/purchase');
  };

  const hasMissingHost = hasMissingRequiredHostApp(cart, serialRowsByItem);
  const emptyOrZero = isCartEmptyOrZero(items);
  const invalidUpgradeSerials = useMemo(
    () => hasInvalidUpgradeSerials(items, serialRowsByItem),
    [items, serialRowsByItem],
  );

  const itemsCount = useMemo(
    () => computeItemsCount(items, serialRowsByItem),
    [items, serialRowsByItem],
  );

  const appliedDiscountCode =
    cart?.items?.find(
      (item: { discountId: { code: any } }) => item.discountId?.code,
    )?.discountId.code || '';

  const {
    discountedLines,
    baseTotal,
    totalDiscount,
    grandTotal,
    discountDescriptors,
    saleBeatsAppliedDiscount,
  } = useMemo(
    () => computeCartTotals(items, serialRowsByItem),
    [items, serialRowsByItem],
  );

  if (!cart || isLoading) return <p>Loading...</p>;

  const checkoutDisabled =
    hasMissingHost ||
    emptyOrZero ||
    invalidUpgradeSerials ||
    cartControlsDisabled;

  return (
    <>
      <Snackbar
        open={Boolean(pageNotice)}
        autoHideDuration={8000}
        onClose={() => setPageNotice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={pageNotice?.severity ?? 'warning'}
          variant="filled"
          onClose={() => setPageNotice(null)}
          sx={{ width: '100%' }}
        >
          {pageNotice?.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: { xs: 2, md: 4 },
          mx: 'auto',
          alignItems: 'start',
        }}
      >
        {cartCompletedMessage && (
          <Alert severity="info" sx={{ gridColumn: '1 / -1' }}>
            {cartCompletedMessage}
          </Alert>
        )}
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            p: { xs: 2, md: 3 },
          }}
        >
          <h2 style={styles.heading}>SHOPPING CART</h2>
          {items && items.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh',
                textAlign: 'center',
                color: '#777',
              }}
            >
              <ShoppingCartOutlinedIcon
                sx={{ fontSize: 64, color: '#ccc', mb: 2 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Your cart is currently empty
              </Typography>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Looks like you haven’t added anything yet.
              </Typography>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#1976d2',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  ← Continue Shopping
                </Typography>
              </Link>
            </Box>
          )}
          {!cart || isLoading ? (
            <p>Loading...</p>
          ) : (
            items.map((item: any) => (
              <CartLineItem
                key={cartItemKey(item)}
                item={item}
                baseUrl={baseUrl}
                serialRowsByItem={serialRowsByItem}
                setSerialRowsByItem={setSerialRowsByItem}
                validateSerialForItem={validateSerialForItem}
                removeSerialRow={removeSerialRow}
                commitUpgradeSerials={commitUpgradeSerials}
                getSupportedPlatformsForItem={getSupportedPlatformsForItem}
                onUpdateCartItem={updateCartItem}
                onQuantityChange={handleQuantityChange}
                onRemoveLine={handleRemove}
                controlsDisabled={cartControlsDisabled}
                lineDisplayTotal={getLineDisplayTotal(item, serialRowsByItem)}
              />
            ))
          )}
          {items && items.length > 0 && (
            <button
              type="button"
              style={{ ...styles.linkButton, marginTop: '20px' }}
              onClick={() => {
                window.location.href = '/#/';
              }}
            >
              ◀ Continue shopping
            </button>
          )}
        </Box>

        <CartOrderSummary
          itemsCount={itemsCount}
          baseTotal={baseTotal}
          grandTotal={grandTotal}
          totalDiscount={totalDiscount}
          discountedLinesLength={discountedLines.length}
          discountDescriptors={discountDescriptors}
          saleBeatsAppliedDiscount={saleBeatsAppliedDiscount}
          hasInvalidUpgradeSerials={invalidUpgradeSerials}
          checkoutDisabled={checkoutDisabled}
          onCheckout={handleCheckout}
          appliedDiscountCode={appliedDiscountCode}
          onDiscountUpdate={() => {
            refetch();
          }}
        />
      </Box>
    </>
  );
};

export default CartPage;
